import React, { useEffect, useState } from 'react';
import { useNotificationStore, type Notification } from '../../store/notificationStore';
import '../styles/NotificationContainer.css';

export interface NotificationContainerProps {
  maxToasts?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  defaultDuration?: number;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  maxToasts = 5,
  position = 'top-right',
  defaultDuration = 5000,
}) => {
  const { notifications, remove } = useNotificationStore();
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);

  useEffect(() => {
    // Show all recent notifications as toasts (including errors which have autoClose: false)
    const toastNotifications = notifications.slice(0, maxToasts);

    setVisibleToasts(toastNotifications);
  }, [notifications, maxToasts]);

  useEffect(() => {
    // Set up auto-dismiss timers for each toast (skip those with autoClose: false)
    const timers = visibleToasts
      .filter((notification) => notification.autoClose !== false)
      .map((notification) => {
        const duration = notification.duration || defaultDuration;

        return setTimeout(() => {
          remove(notification.id);
        }, duration);
      });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [visibleToasts, remove, defaultDuration]);

  const handleClose = (id: string) => {
    remove(id);
  };

  const getToastIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg className="toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'pending':
        return (
          <svg className="toast-icon toast-icon-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
      default:
        return (
          <svg className="toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if (visibleToasts.length === 0) {
    return null;
  }

  return (
    <div className={`notification-toast-container notification-toast-${position}`}>
      {visibleToasts.map((notification) => (
        <div
          key={notification.id}
          className={`notification-toast notification-toast-${notification.type}`}
          role="alert"
        >
          {/* Icon */}
          <div className={`toast-icon-wrapper toast-icon-${notification.type}`}>
            {getToastIcon(notification.type)}
          </div>

          {/* Content */}
          <div className="toast-content">
            <h4 className="toast-title">{notification.title}</h4>
            <p className="toast-message">{notification.message}</p>
            {notification.transactionHash && (
              <a
                href={`https://livenet.xrpl.org/transactions/${notification.transactionHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="toast-tx-link"
              >
                View transaction →
              </a>
            )}
          </div>

          {/* Close Button */}
          <button
            className="toast-close"
            onClick={() => handleClose(notification.id)}
            aria-label="Close notification"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};
